import { useState } from 'react';
import { useTranslation } from '../../lib/i18n';
import { useCodeWaitCarry } from '../../lib/code-wait-carry';
import { CodeVerificationInput, useCodeVerification } from '../../lib/use-code-verification';
import { useGenericBi } from '../../lib/use-generic-bi';
import { useItemSeenBi } from '../../lib/use-item-seen-bi';
import { usePostPurchasePhoneGate } from '../../lib/use-post-purchase-phone-gate';
import { Button, Typography } from './ui';
import { CodeStepBody } from './onboarding/CodeScreen';
import { PhoneStepBody } from './onboarding/PhoneScreen';
import { SignupShell } from './onboarding/SignupShell';
import styles from './PostPurchasePhoneGate.module.css';

/**
 * The post-purchase ask for a phone number — web's half of a three-surface feature
 * (`docs/claude/post-purchase-phone-verification.md`).
 *
 * Web had no blocking screen of any kind; its phone connect is a dismissible dialog over the current
 * page. iOS and Android already take the whole screen for this (`RootContentState.phoneVerification` /
 * `AppRootContent.PHONE_VERIFICATION`), so this is web catching up to the pair rather than a new
 * pattern — and it is built out of the SAME bodies the onboarding step uses (`PhoneStepBody`,
 * `CodeStepBody`, `SignupShell`), so the two cannot drift into different asks for the same thing.
 *
 * It reads as a hard block and is not one: the way out appears after a minute. See
 * `skipVisible` for why that reveal fails toward showing.
 */
export function PostPurchasePhoneGate() {
  const { t } = useTranslation();
  const { open, canSkip, skip } = usePostPurchasePhoneGate();
  const { logPageClickBi } = useGenericBi();
  /**
   * The IMPRESSION, and the reason this exists rather than a page view: the body already reports one
   * (`usePhoneVerification` → `usePageLoadBi(PHONE_VERIFY_PAGE)`, `verify_phone_number_page`), and that
   * same page view is emitted by the onboarding step and the connect dialog. Three surfaces, one name —
   * so nothing in the data could say the post-purchase ask had ever been shown, which is exactly what
   * was asked of it on its first day live and could not be answered.
   *
   * This is the DENOMINATOR: the question about this feature is a conversion rate — of the people shown
   * the ask, how many give a number and how many skip — and the skip click was shipped without anything
   * to divide it by. A mount effect would not do, because the gate can render off-screen; the viewport
   * observer is what makes it an impression rather than a render count.
   */
  const gateSeenRef = useItemSeenBi('post-purchase-phone-gate');
  const [sent, setSent] = useState<CodeVerificationInput['sent']>(undefined);
  // The code step's wait, carried to the number step when the same number is entered again. The gate's
  // own, emptied whenever the gate is not open (see `useCodeWaitCarry`).
  const codeWait = useCodeWaitCarry(open);

  const code = useCodeVerification({
    sent,
    codeWait,
    // Nothing to do on success beyond letting the gate close itself: the verified number lands on the
    // recipient, the recipient read refreshes, and `shouldClearLatch` drops the latch. Closing this
    // screen from here as well would race that and flash the app underneath.
    onUseAnotherNumber: () => setSent(undefined),
  });

  if (!open) return null;

  return (
    // Deliberately carries no dialog role. This is a SCREEN — the same thing iOS and Android present
    // as a root content state — not something floating over a page the reader can return to. The
    // shared Modal shell is what a real dialog uses here, and `test/modal-usage.test.ts` is right to
    // refuse a hand-rolled one; the answer is that this is not one, not that it should fake being one.
    <div className={styles.takeover} ref={gateSeenRef}>
      <SignupShell>
        {/* SignupShell's field is a centred flex ROW, so its children lay out side by side — the head
            rendered BESIDE the form rather than above it. One column wrapper is the whole fix; the
            shell keeps doing the centring. */}
        <div className={styles.body}>
        {/*
          The heading is the gate's own, not the body's: `PhoneStepBody` is only the country + number
          form, and every other caller (the onboarding step, PhoneVerifyModal) supplies its own title
          and subtitle around it. Without one this screen was a bare input field with no statement of
          what it wanted or why — which is the first thing a person who has just paid would ask.

          It reuses the onboarding strings rather than minting new ones, so the copy is already
          translated in all 37 locales and says the same thing the other phone asks say. A
          recovery-specific line ("so you can get back into the account you just paid for") would read
          better here and is a deliberate follow-up, because it needs 37 real translations.
        */}
        <div className={styles.head}>
          <Typography variant="h3" align="center">{t('onboarding.phone.title')}</Typography>
          <Typography variant="p2" align="center" className={styles.subtitle}>
            {t('onboarding.phone.subtitle')}
          </Typography>
        </div>
        {sent ? <CodeStepBody code={code} /> : <PhoneStepBody onCodeSent={setSent} codeWait={codeWait} />}
        {/*
          The way out, hidden for the first minute. It is rendered only once visible rather than
          disabled-then-enabled: a greyed-out Skip advertises that there is an escape and refuses it,
          which is a worse experience than a screen that simply does not offer one yet.
        */}
        {canSkip ? (
          <div className={styles.skip}>
            <Button
              variant="text"
              onClick={() => {
                logPageClickBi({ page_name: 'post_purchase_phone', button_name: 'skip' });
                skip();
              }}
            >
              {t('onboarding.phone.skip')}
            </Button>
          </div>
          ) : null}
        </div>
      </SignupShell>
    </div>
  );
}
